import { apiClient } from './client';
import type { ApiResponse, Source, SourceStats, FetchResponse, QueryResponse, BatchQueryResponse } from '../types';

export interface GetDataParams {
  limit?: number;
  offset?: number;
  filter?: Record<string, unknown>;
}

export interface QueryParams {
  sourceId: number;
  parameters?: Record<string, unknown>;
  timeout?: number;
}

export interface BatchQueryItem {
  key: string;
  sourceId: number;
  parameters?: Record<string, unknown>;
}

export const dataApi = {
  // List all sources
  getSources: () =>
    apiClient.get<ApiResponse<Source[]>>('/api/v1/sources'),

  // Get data from a source with pagination and filtering
  getData: <T = unknown>(sourceName: string, params?: GetDataParams) =>
    apiClient.get<ApiResponse<T[]>>(`/api/v1/data/${sourceName}`, { params }),

  // Get statistics for a source
  getStats: (sourceName: string) =>
    apiClient.get<ApiResponse<SourceStats>>(`/api/v1/data/${sourceName}/stats`),

  // Trigger a parameterized fetch (fire-and-forget, saves to DB)
  triggerFetch: (sourceId: number, parameters?: Record<string, unknown>) =>
    apiClient.post<ApiResponse<FetchResponse>>('/api/v1/fetch', {
      sourceId,
      parameters
    }),

  // Get fetch job status
  getFetchStatus: (jobId: string) =>
    apiClient.get<ApiResponse<FetchResponse>>(`/api/v1/fetch/${jobId}`),

  // Execute direct query and get results synchronously (no DB storage)
  executeQuery: <T = unknown>(sourceId: number, parameters?: Record<string, unknown>, timeout?: number) =>
    apiClient.post<QueryResponse<T>>('/api/v1/query', {
      sourceId,
      parameters,
      timeout: timeout ?? 180
    }),

  // Execute batch query - multiple queries in a single request
  executeBatchQuery: (queries: BatchQueryItem[], timeout?: number) =>
    apiClient.post<BatchQueryResponse>('/api/v1/query/batch', {
      queries,
      timeout: timeout ?? 180,
    }),

  // Dismiss an announcement
  dismissAnnouncement: (announcementId: number) =>
    apiClient.post(`/api/v1/announcements/${announcementId}/dismiss`),

  // Execute query with localStorage fallback for agent offline scenarios
  executeQueryWithFallback: async <T = unknown>(
    sourceId: number,
    parameters?: Record<string, unknown>,
    timeout?: number,
  ): Promise<{ data: QueryResponse<T>; isFromCache: boolean }> => {
    const cacheKey = `raporgor-query-${sourceId}-${JSON.stringify(parameters || {})}`;
    try {
      const response = await apiClient.post<QueryResponse<T>>('/api/v1/query', {
        sourceId,
        parameters,
        timeout: timeout ?? 180,
      });
      // Cache successful response
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          data: response.data,
          timestamp: Date.now(),
        }));
      } catch { /* quota exceeded - ignore */ }
      return { data: response.data, isFromCache: false };
    } catch (error) {
      // Try to return cached data on failure
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const entry = JSON.parse(cached);
          return { data: entry.data, isFromCache: true };
        }
      } catch { /* parse error - ignore */ }
      throw error;
    }
  },
};
